import {
  authenticationBody, isAuthentication, renderTemplate,
  type TemplateDraft, type TemplateDraftButton,
} from '@wa/shared';

/**
 * The message as WhatsApp will draw it, filled with the sample values.
 *
 * Worth the space: nearly every template rejection is about wording, and reading
 * a body with {{1}} in it is not how anyone spots that. Samples are substituted
 * so what is on screen is a sentence, not a form.
 */
export function TemplatePreview({ draft }: { draft: TemplateDraft }) {
  const auth = isAuthentication(draft);

  const body = auth ? authenticationBody(draft) : renderTemplate(draft.body, draft.bodyExamples);

  const header = !auth && draft.headerFormat === 'TEXT'
    ? renderTemplate(draft.headerText ?? '', [draft.headerExample ?? ''])
    : null;

  // Meta writes both the footer and the button label on an authentication
  // template, so the preview shows Meta's wording rather than an empty slot.
  const footer = auth
    ? (draft.codeExpirationMinutes ? `This code expires in ${draft.codeExpirationMinutes} minutes.` : null)
    : draft.footer;

  const buttons: TemplateDraftButton[] = auth
    ? [{
        type: draft.otpType === 'ONE_TAP' ? 'URL' : 'COPY_CODE',
        text: draft.otpButtonText || (draft.otpType === 'ONE_TAP' ? 'Autofill' : 'Copy code'),
        // The label above is already the text to draw, so it doubles as the
        // copy-code sample the preview renders for a real copy-code button.
        example: draft.otpButtonText || 'Copy code',
      }]
    : draft.buttons;

  return (
    <div className="chat-ground min-h-0 flex-1 overflow-y-auto p-4">
      <div className="ml-auto w-fit max-w-[min(26rem,92%)] rounded-lg rounded-br-sm bg-(--color-bubble-out) px-2.5 py-2 text-body leading-snug shadow-sm">
        {!auth && draft.headerFormat !== 'NONE' && draft.headerFormat !== 'TEXT' && (
          <div className="mb-1.5 grid h-24 place-items-center rounded bg-black/10 text-micro tracking-wide text-(--color-muted) uppercase">
            {draft.headerFormat}
          </div>
        )}

        {header && <p className="mb-1 font-semibold break-words">{header}</p>}

        {body
          ? <p className="break-words whitespace-pre-wrap">{body}</p>
          : <p className="text-(--color-muted) italic">The message body appears here.</p>}

        {auth && (
          <p className="mt-1 text-micro text-(--color-muted) italic">
            WhatsApp writes and translates this wording.
          </p>
        )}

        {footer && (
          <p className="mt-1.5 text-micro break-words text-(--color-muted)">{footer}</p>
        )}
      </div>

      {buttons.length > 0 && (
        <div className="ml-auto mt-1 flex w-fit max-w-[min(26rem,92%)] flex-col gap-1">
          {buttons.map((button, index) => (
            <span
              key={index}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-(--color-bubble-out) px-2.5 py-1.5 text-center text-small text-(--color-brand) shadow-sm"
            >
              {/* WhatsApp draws a small mark on link, call and copy buttons.
                  These are the same three, simplified enough to stay legible at
                  13px rather than collapsing into a smudge. */}
              {button.type === 'URL' && (
                <Glyph d="M8 12l4-4M6.5 9.5L5 11a2.8 2.8 0 0 0 4 4l1.5-1.5M13.5 10.5L15 9a2.8 2.8 0 0 0-4-4L9.5 6.5" />
              )}
              {button.type === 'PHONE_NUMBER' && (
                <Glyph d="M6.2 3.8l1.7 2.6-1.3 1.3a8.5 8.5 0 0 0 4.7 4.7l1.3-1.3 2.6 1.7-.8 2.4a1.2 1.2 0 0 1-1.3.8A12 12 0 0 1 3.2 5.3a1.2 1.2 0 0 1 .8-1.3z" />
              )}
              {button.type === 'COPY_CODE' && (
                <Glyph d="M8 8h6.5v6.5H8zM5.5 11.5V5.5H12" />
              )}
              <span className="truncate">
                {button.type === 'COPY_CODE' ? (button.example || 'Copy code') : (button.text || '—')}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Glyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" className="shrink-0">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
